import {
  keepPreviousData,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { api } from 'lib/request/api';
import type {
  ModelMapResponse,
  SlotValuationInputsResponse,
  TearsheetErrorCode,
  TearsheetErrorReason,
  TearsheetRemedy,
} from 'types/packages-api/slot-valuation-inputs';
import { useCalcLifecycle, type CalcRunState } from '../hooks';
import type { SlotOverrideLogEntry } from '../types';
import { toSlotOverridesWire } from '../utils';

/**
 * Endpoint paths for the two Eval-Tool-Scrutability reads. No leading slash — the
 * `api` (ky) instance sets `prefixUrl`, so paths must be relative (INS-11820 / INS-11822).
 *
 *   slot_valuation_inputs — per-slot, varies every request (the tearsheet payload)
 *   model_map             — the registry, identical for every caller (the diagram)
 */
export const SLOT_VALUATION_INPUTS_URL =
  'queries/packages/slot_valuation_inputs';
export const MODEL_MAP_URL = 'queries/model_map';

/** Query-key prefix for the per-slot tearsheet payload (scoped invalidation). */
export const SLOT_VALUATION_INPUTS_QUERY_KEY =
  'eval3-new:slot-valuation-inputs' as const;

/** Query-key for the registry model map — no dimensions, it never varies. */
export const MODEL_MAP_QUERY_KEY = 'eval3-new:model-map' as const;

/**
 * Longer-than-default timeout for the tearsheet reads. `slot_valuation_inputs`
 * reads the completed calc's frozen blob (large for big packages), so ky's 30s
 * default can bite; the sibling eval3-new endpoints raise the same ceiling.
 *
 * EXPORTED for the export POST, which needs the same ceiling for a stronger
 * reason than the reads do (tearsheet-export-button.tsx). One constant, so the
 * read budget and the write budget cannot drift apart.
 */
export const TEARSHEET_REQUEST_TIMEOUT_MS = 120_000;

/**
 * A typed `slot_valuation_inputs` failure. The endpoint returns a structured
 * `{ code, retryable?, reason?, remedy?, missingKeys?, message? }` envelope on
 * 4xx/5xx (§ Errors of the contract). Parsed off the ky `HTTPError` so the panel
 * can distinguish a retryable 503 from a permanent 422 and compose copy from the
 * machine-readable `code`/`reason`/`remedy` (the backend sends no user copy).
 */
export class TearsheetApiError extends Error {
  code?: TearsheetErrorCode;
  retryable: boolean;
  reason?: TearsheetErrorReason;
  remedy?: TearsheetRemedy;
  missingKeys?: string[];

  constructor(
    message: string,
    fields: {
      code?: TearsheetErrorCode;
      retryable?: boolean;
      reason?: TearsheetErrorReason;
      remedy?: TearsheetRemedy;
      missingKeys?: string[];
    } = {}
  ) {
    super(message);
    this.name = 'TearsheetApiError';
    this.code = fields.code;
    // Only the 503 is worth retrying (contract: `retryable` is load-bearing).
    this.retryable = fields.retryable ?? false;
    this.reason = fields.reason;
    this.remedy = fields.remedy;
    this.missingKeys = fields.missingKeys;
  }
}

/** The envelope fields, each present only if the body actually carried the right type. */
interface TearsheetErrorEnvelope {
  code?: TearsheetErrorCode;
  retryable?: boolean;
  reason?: TearsheetErrorReason;
  remedy?: TearsheetRemedy;
  missingKeys?: string[];
  message?: string;
}

/**
 * True for a non-null, non-array object — the only shape whose keys can be read.
 *
 * @param {unknown} value - The value to narrow.
 * @return {boolean} Whether `value` is a readable record.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Narrows a parsed error body to {@link TearsheetErrorEnvelope}.
 *
 * ── TWO NESTING LEVELS, AND A RUNTIME CHECK RATHER THAN A CAST ────────────────────────
 *
 * The endpoint renders the envelope NESTED: `{ error: { code, message, retryable, … } }`
 * (structured_error_rendering.rb:20-22, and the backend spec asserts `body['error'][…]`
 * at slot_valuation_inputs_spec.rb:130-187). This function previously read the TOP level
 * through a bare `as` cast, so every field resolved to `undefined`, `retryable` fell to
 * its `?? false` default, and the retryable 503 rendered as a permanent error with no
 * retry and ky's generic status message. The cast is what made a shape mismatch silent:
 * it asserted a shape instead of checking one, so there was nothing to fail.
 *
 * Both levels are accepted deliberately. The nested shape is what ships today; the
 * top-level fallback means an unwrapped envelope — a plausible future change, since the
 * `error` wrapper is a convention of the shared renderer and not of this endpoint —
 * degrades to working rather than to silence.
 *
 * Every field is type-checked individually and dropped when it does not match, so a
 * malformed body yields absent fields (permanent, no retry) rather than a truthy
 * `retryable` read off a string.
 *
 * @param {unknown} parsed - The JSON-parsed response body, or null when unparseable.
 * @return {TearsheetErrorEnvelope} The fields that were actually present and well-typed.
 */
export function readTearsheetErrorEnvelope(
  parsed: unknown
): TearsheetErrorEnvelope {
  if (!isRecord(parsed)) {
    return {};
  }
  const source = isRecord(parsed.error) ? parsed.error : parsed;
  return {
    code:
      typeof source.code === 'string'
        ? (source.code as TearsheetErrorCode)
        : undefined,
    retryable:
      typeof source.retryable === 'boolean' ? source.retryable : undefined,
    reason:
      typeof source.reason === 'string'
        ? (source.reason as TearsheetErrorReason)
        : undefined,
    remedy:
      source.remedy === 'rerun_evaluation' ? 'rerun_evaluation' : undefined,
    missingKeys: Array.isArray(source.missingKeys)
      ? source.missingKeys.filter(
          (key): key is string => typeof key === 'string'
        )
      : undefined,
    message: typeof source.message === 'string' ? source.message : undefined,
  };
}

/**
 * Parses the structured tearsheet error envelope off a thrown ky `HTTPError`, so
 * callers get `{ code, retryable, reason, remedy }` rather than a bare status.
 * Falls back to the ky message for a non-HTTP error (timeout / network) or an
 * unparseable body.
 *
 * @param {unknown} err - The error thrown by the query function.
 * @return {Promise<TearsheetApiError>} The normalized tearsheet error.
 */
async function parseTearsheetError(err: unknown): Promise<TearsheetApiError> {
  if (err instanceof HTTPError) {
    const body = readTearsheetErrorEnvelope(
      await err.response.json().catch(() => null)
    );
    return new TearsheetApiError(body.message ?? err.message, {
      code: body.code,
      retryable: body.retryable,
      reason: body.reason,
      remedy: body.remedy,
      missingKeys: body.missingKeys,
    });
  }
  return new TearsheetApiError(
    err instanceof Error ? err.message : 'Request failed'
  );
}

/** Inputs to {@link useSlotValuationInputs}. */
interface SlotValuationInputsArgs {
  packageId: number;
  /** The eval-calc id. `null` in package mode — the tearsheet cannot load then. */
  calculationId: number | null;
  /** The slot's row identity (`TractNRI.slotId`). `null` when nothing is open. */
  slotId: string | null;
  /** Gate — pass `false` (e.g. panel closed) to defer the fetch. */
  enabled?: boolean;
  /**
   * The working config's PENDING override operations, in log order — the edits the user has
   * made since the last Calculate.
   *
   * WITHOUT THESE THE TEARSHEET CANNOT SEE ITS OWN EDITS. The endpoint resolves them over the
   * run's committed log for that response only and persists nothing
   * (slot_valuation_inputs_controller.rb:57-69). Omitting them was the whole of the "an edit
   * from the tearsheet does not save" defect: the write landed in the working log and the
   * read answered as though it had not, so no badge appeared and no value moved, no matter
   * how often the panel was reopened.
   *
   * Absent (or empty) means this surface has no pending log — not "there are none".
   */
  slotOverrides?: SlotOverrideLogEntry[];
  /**
   * The SCHEDULE DIALOG's committed per-slot date edits — the second override channel.
   *
   * ⚠️ ALREADY WIRE-SHAPED, unlike {@link slotOverrides}. The working config holds these as
   * the dialog persisted them (`{type:'individual', slot_id, overrides}`), which is the shape
   * the backend parses, so they are NOT put through `toSlotOverridesWire` — passing them
   * through it a second time is what would drop them.
   *
   * WITHOUT THESE THE TEARSHEET'S BOUNDS ARE STALE. Calculate composes the two channels
   * (utils.tsx, `buildEvaluationPayload`) with the dialog's applied LAST; the tearsheet sent
   * only its own, so a date edited in the dialog was invisible here and the picker clamped to
   * a neighbour the run would not use — then Calculate rejected the value the picker had just
   * offered, with a 422.
   */
  scheduleSlotOverrides?: Array<Record<string, unknown>>;
}

/**
 * Fetches one slot's tearsheet payload (`POST /queries/packages/slot_valuation_inputs`)
 * — the header, the seven domain groups, the resolved slot-input nodes, and the
 * outputs (§ contract). Keyed on `packageId` + `calculationId` + `slotId` so every
 * slot caches apart. Calc-mode only: the values live in the completed calc's frozen
 * blob, so the fetch is gated on a completed calc (like `chart_series`) — package
 * mode / a running calc never fires a doomed request. A structured
 * {@link TearsheetApiError} surfaces the retryable-503 vs permanent-422 split.
 *
 * @param {SlotValuationInputsArgs} args - The package / calc / slot identity + enable gate.
 * @return {UseQueryResult<SlotValuationInputsResponse, TearsheetApiError>} The query result.
 */
export function useSlotValuationInputs({
  packageId,
  calculationId,
  slotId,
  slotOverrides,
  scheduleSlotOverrides,
  enabled = true,
}: SlotValuationInputsArgs): UseQueryResult<
  SlotValuationInputsResponse,
  TearsheetApiError
> & { calcState: CalcRunState } {
  // The values exist only for a COMPLETED calc (the frozen blob) — gate on
  // `completed`, not `canFetch` (which also allows package mode, where the
  // endpoint 422s `calculation_required`).
  const { state } = useCalcLifecycle(calculationId);
  // EMPTY IS ABSENT HERE. A surface with no pending edits and one whose edits were just
  // committed must produce the SAME key and the same body, or committing would leave a
  // `slotOverrides: []` cache entry sitting beside the identical `undefined` one.
  // CONVERTED THROUGH THE SHARED SERIALISER, not hand-shaped. The working config holds
  // `{kind:'slot', slotId, overrides}`; the wire wants `{type:'individual', slot_id,
  // overrides}`. master_data sends the same log through the same function, so the tearsheet
  // cannot drift into a second spelling of the one thing the backend parses.
  //
  // ── ⚠️ BOTH CHANNELS, IN CALCULATE'S ORDER ─────────────────────────────────────────────
  //
  // The schedule dialog's date edits ride a SECOND channel, and the tearsheet sent only the
  // first — so its bounds were computed without them and Calculate 422'd the day the picker
  // had just offered. They are appended AFTER the slot-table ops, which is exactly what
  // `buildEvaluationPayload` does, so last-wins resolves here the way it will at Calculate.
  //
  // NOT through `toSlotOverridesWire`: the dialog's ops are already in wire shape. Only the
  // working config's own log needs converting.
  const pendingWire = [
    ...(slotOverrides?.length ? toSlotOverridesWire(slotOverrides) : []),
    ...(scheduleSlotOverrides ?? []),
  ];
  const pending = pendingWire.length ? pendingWire : undefined;

  const query = useQuery<SlotValuationInputsResponse, TearsheetApiError>({
    // THE PENDING LOG IS PART OF THE KEY, and it has to be. The payload is held at
    // `staleTime: Infinity`, and `commitSlotOverride` invalidates this cache with
    // `refetchType: 'none'` — which marks an OPEN tearsheet stale without refetching it. So
    // an edit made while the panel is open would repaint only if the key itself changed.
    // Keyed on the log, each distinct set of pending edits is its own entry and a new edit
    // fetches immediately; react-query hashes the key by VALUE, so the new array identity a
    // re-render produces does not refetch on its own.
    //
    // ⚠️ `pending` IS BOTH CHANNELS, which is what makes a SCHEDULE-DIALOG edit repaint an
    // open tearsheet. Keying on the tearsheet's own channel alone would leave the key
    // unchanged when the dialog moved a date — and the failure is silent: no error, no
    // refetch, a panel quietly showing bounds from before the edit.
    queryKey: [
      SLOT_VALUATION_INPUTS_QUERY_KEY,
      packageId,
      calculationId ?? null,
      slotId ?? null,
      pending ?? null,
    ],
    queryFn: async ({ signal }) => {
      try {
        // Flat body — Rails `wrap_parameters` wraps it under `query` server-side,
        // matching the sibling Query::Packages endpoints. `calculationId` is
        // required (the backend resolves the run's UUID itself and ignores any).
        return await api
          .post(SLOT_VALUATION_INPUTS_URL, {
            json: {
              packageId,
              calculationId,
              slotId,
              // `slot_overrides`, SNAKE_CASE — the one spelling the backend reads
              // (Queries::Packages::SlotOverrideParams reads
              // `params[:query][:slot_overrides]`). A camelCase key is not rejected, it is
              // SILENTLY IGNORED: the request succeeds, the pending edits are dropped, and
              // the response looks exactly like one from a slot with no overrides. Every
              // other eval3-new surface sends this same key for the same reason.
              ...(pending ? { slot_overrides: pending } : {}),
            },
            signal,
            timeout: TEARSHEET_REQUEST_TIMEOUT_MS,
          })
          .json<SlotValuationInputsResponse>();
      } catch (err) {
        throw await parseTearsheetError(err);
      }
    },
    enabled:
      enabled && state === 'completed' && calculationId !== null && !!slotId,
    // Per-slot payload is stable within a completed calc (the blob is frozen), so
    // it never goes stale for the life of the calc.
    staleTime: Infinity,
    // ── ⚠️ THE PANEL DOES NOT BLANK ON AN EDIT ──────────────────────────────────────────
    //
    // "Every tearsheet edit or revert blanks the panel to a skeleton. The pending log is in
    // the query key without keepPreviousData, so the table unmounts and loses scroll."
    //
    // The pending log IS in the key, deliberately — that is what makes an edit repaint. But a
    // new key with no cached entry means `data` is undefined for the round trip, so the whole
    // table unmounted and came back scrolled to the top, on every edit and every revert.
    //
    // The previous payload is the right thing to hold: it is the same slot in the same
    // calculation, one pending entry behind. `isFetching` is already what the panel reads for
    // its in-flight state, so the difference is visible without the unmount. Same option
    // `value-rollup-table-card` and the cross-section hooks use for a keyed refetch.
    placeholderData: keepPreviousData,
    // Only the 503 is worth retrying (contract). React Query's retry runs before
    // our error is thrown, so branch on the ky status directly.
    retry: (failureCount, error) =>
      error instanceof TearsheetApiError && error.retryable && failureCount < 2,
    // AC 4: the permanent state must not be polled. `retry` above already refuses a
    // non-retryable error, there is no `refetchInterval` anywhere under eval3-new, and
    // `refetchOnWindowFocus` is false app-wide (app-shell/app.tsx:19-26) — but React
    // Query leaves `refetchOnReconnect` at TRUE by default, so a network blip would
    // re-issue a permanently-doomed 422. Off here rather than app-wide: this is a
    // statement about this query's frozen-blob payload, not about every table in the app.
    refetchOnReconnect: false,
  });

  // Surface the lifecycle state so the panel can distinguish a still-resolving calc (keep the
  // skeleton) from a processing / error calc (say so) rather than rendering a blank body — the
  // query is DISABLED for every non-completed state, so React Query's own flags cannot tell
  // them apart (a disabled query looks identical whether the calc is running or dead).
  return { ...query, calcState: state };
}

/**
 * Fetches the registry model map (`POST /queries/model_map`) — `{ zones, nodes,
 * edges, meta }`, identical for every caller. Cached against ONE key with
 * `staleTime: Infinity` so it is fetched once per session and every tearsheet
 * shares the single cache entry (the contract's "fetch the map once" — React
 * Query's dedupe is the FE equivalent of the `registryDigest` / `If-None-Match`
 * revalidation it describes).
 *
 * NOT deferred by default: `enabled` defaults to `true`, and the table view's
 * `useNodeMeta` calls this with no argument, so the map request fires as soon as the
 * tearsheet opens, in parallel with the tearsheet read, whichever tab is active. That is
 * intended — the table needs each node's unit/source/category and the Export button stays
 * disabled until they land. An `enabled: false` caller can still defer it; nothing does today.
 *
 * @param {object} [options] - Optional `enabled` gate. Defaults to `true` (fires on open);
 *   pass `false` to defer the fetch until a caller opts in.
 * @return {UseQueryResult<ModelMapResponse, TearsheetApiError>} The query result.
 */
export function useModelMap(options?: {
  enabled?: boolean;
}): UseQueryResult<ModelMapResponse, TearsheetApiError> {
  return useQuery<ModelMapResponse, TearsheetApiError>({
    queryKey: [MODEL_MAP_QUERY_KEY],
    queryFn: async ({ signal }) => {
      try {
        // No parameters — the registry does not vary by package, calc or slot.
        return await api
          .post(MODEL_MAP_URL, {
            json: {},
            signal,
            timeout: TEARSHEET_REQUEST_TIMEOUT_MS,
          })
          .json<ModelMapResponse>();
      } catch (err) {
        throw await parseTearsheetError(err);
      }
    },
    enabled: options?.enabled ?? true,
    staleTime: Infinity,
  });
}
